import {
  AiSummaryInstructions,
  DraftSources,
  HourlyCost,
  PmMembersInfiniteData,
  MembershipEntity,
  ListResponse,
  MembershipCreateValues,
  MembershipUpdateValues,
  PmMembersParams,
  UserEntity,
  MembershipStatus,
} from '@data';
import { api } from '@data/store';

export const fetchMembership = (membershipId: string): Promise<MembershipEntity> =>
  api.get(`/memberships/${membershipId}`);

export const updateMembership = (membershipId: string, values: MembershipUpdateValues): Promise<MembershipEntity> =>
  api.patch(`/memberships/${membershipId}`, values);

export const removeMembership = (membershipId: string): Promise<void> => api.delete(`/memberships/${membershipId}`);

// Its own route rather than a field on the patch above: this switch is yours to set for yourself, and
// that patch is the admin's form for editing somebody else.
export const setDailyNudge = (membershipId: string, isDailyNudgeEnabled: boolean): Promise<MembershipEntity> =>
  // Silent: the control that set it already shows the choice, so a toast would only repeat it.
  api.patch(`/memberships/${membershipId}/daily-nudge`, { isDailyNudgeEnabled }, { silentSuccess: true });

export type ReminderTimeInput = { reminderTime: string; reminderTimesByDay: string[] | null };

export const setReminderTime = (membershipId: string, input: ReminderTimeInput): Promise<MembershipEntity> =>
  api.patch(`/memberships/${membershipId}/reminder-time`, input);

// Its own route both ways: the field never rides on the membership the organization read returns.
export const getAiSummaryInstructions = (membershipId: string): Promise<AiSummaryInstructions> =>
  api.get(`/memberships/${membershipId}/ai-summary-instructions`);

export const setAiSummaryInstructions = (
  membershipId: string,
  aiSummaryInstructions: string,
): Promise<AiSummaryInstructions> =>
  api.patch(`/memberships/${membershipId}/ai-summary-instructions`, { aiSummaryInstructions });

export const setDraftSources = (membershipId: string, sources: Partial<DraftSources>): Promise<AiSummaryInstructions> =>
  api.patch(`/memberships/${membershipId}/draft-sources`, sources);

export const getHourlyCost = (membershipId: string): Promise<HourlyCost> =>
  api.get(`/memberships/${membershipId}/hourly-cost`);

/** `null` clears it. */
export const setHourlyCost = (membershipId: string, hourlyCost: number | null): Promise<HourlyCost> =>
  api.patch(`/memberships/${membershipId}/hourly-cost`, { hourlyCost });

// Same reasoning, and the route this has to use: the admin patch answers 403 for anyone who is not an
// admin, so setting your own chapter through it never worked for the people it is for. `null` clears it.
export const setDefaultChapter = (membershipId: string, chapter: string | null): Promise<MembershipEntity> =>
  api.patch(`/memberships/${membershipId}/default-chapter`, { chapter });

export const createMembership = (organizationId: string, values: MembershipCreateValues): Promise<MembershipEntity> =>
  api.post(`/organizations/${organizationId}/memberships`, values);

export const fetchPmMembersInfinite = async (
  organizationId: string,
  params: PmMembersParams,
  { pageParam },
): Promise<PmMembersInfiniteData> => {
  const newParams = pageParam ? { ...params, ...pageParam } : { ...params };

  const data = await api.get(`organizations/${organizationId}/pm/memberships`, {
    params: newParams,
  });

  return {
    sprintNumber: newParams?.sprintNumber,
    sprintId: newParams?.sprintId,
    from: newParams?.from,
    to: newParams?.to,
    items: data.items,
  };
};
export const fetchPmMembers = async (
  organizationId: string,
  params: PmMembersParams,
): Promise<ListResponse<MembershipEntity>> => {
  const data = await api.get(`organizations/${organizationId}/pm/memberships`, {
    params,
  });

  return data;
};

export const sendMembershipReminder = async (membershipId?: string): Promise<void> => {
  await api.post(`memberships/${membershipId}/reminder`);
};

export const updateUser = (userId: string, activeStatus: MembershipStatus): Promise<UserEntity> =>
  api.patch(`/users/${userId}`, { activeStatus });
